import React, { ChangeEvent, useState, useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import styled from "@emotion/styled";

const InputContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  // width: "100%",
});

const InputWrapper = styled.div({
  position: "relative",
  display: "flex",
  alignItems: "center",
  // gap: "1rem",
  flex: 1,
});

const Input = styled.input<{ maxWidth?: string; align?: string }>(
  ({ theme, width, height, maxWidth, align }) => ({
    width: width,
    maxWidth: maxWidth ? maxWidth : "",
    height: height,
    border: `1px solid ${theme.color.light_gray}`,
    padding: "1.6rem",
    borderRadius: "0",
    fontSize: "1.6rem",
    textAlign: (align as any) || "left",
  }),
);

interface CustomTextInputProps {
  name: string;
  type?: string;
  errorMessage?: string;
  width?: string;
  height?: string;
  placeholder?: string;
  alwaysShowMessage?: boolean;
  message?: string;
  serverError?: string;
  serverSuccess?: boolean;
  value?: string | number;
  readOnly?: boolean;
  hidden?: boolean;
  children?: React.ReactNode;
  error?: boolean;
  maxWidth?: string;
  min?: number;
  max?: number;
  mode?:
    | "none"
    | "text"
    | "decimal"
    | "numeric"
    | "tel"
    | "search"
    | "email"
    | "url";
  dir?: "rtl" | "ltr";
}

const formatWithCommas = (value: string): string => {
  if (!value) return "";
  const dotIndex = value.indexOf(".");
  const intPart = dotIndex >= 0 ? value.slice(0, dotIndex) : value;
  const decPart = dotIndex >= 0 ? value.slice(dotIndex) : "";
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedInt + decPart;
};

const NumberInput: React.FC<CustomTextInputProps> = ({
  name,
  type = "number",
  height = "5.2rem",
  width = "100%",
  placeholder = "",
  maxWidth,
  value,
  readOnly = false,
  hidden,
  children,
  mode = "numeric",
  dir = "ltr",
  max,
}) => {
  const { setValue, watch } = useFormContext();
  const [rawValue, setRawValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const currentValue = watch(name);

  // form 값이 외부에서 변경될 때 rawValue 동기화 (초기 로딩, reset 등)
  useEffect(() => {
    if (!isFocused) {
      if (currentValue === null || currentValue === undefined || currentValue === "") {
        setRawValue("");
      } else if (!isNaN(Number(currentValue))) {
        setRawValue(String(currentValue));
      }
    }
  }, [currentValue, isFocused]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/,/g, "");

    // 빈 입력
    if (input === "") {
      setRawValue("");
      setValue(name, null);
      return;
    }

    // 숫자와 소수점만 허용, 소수점 1개만
    if (!/^\d*\.?\d*$/.test(input)) return;

    setRawValue(input);

    // "0." 같은 불완전한 소수점 입력 중에는 정수값만 form에 전달
    const numValue = Number(input);
    if (!isNaN(numValue)) {
      setValue(name, numValue);
    }
  }, [name, setValue]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // blur 시 불완전한 소수점 정리 (예: "5." → "5")
    if (rawValue.endsWith(".")) {
      setRawValue(rawValue.slice(0, -1));
    }
  }, [rawValue]);

  const displayValue = isFocused
    ? rawValue
    : formatWithCommas(rawValue);

  return (
    <InputContainer>
      <InputWrapper>
        <Input
          id={name}
          type="text"
          name={name}
          height={height}
          width={width}
          placeholder={placeholder}
          value={displayValue}
          readOnly={readOnly}
          hidden={hidden}
          inputMode={mode}
          maxWidth={maxWidth}
          align={dir === "rtl" ? "right" : "left"}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={max}
        />

        <span>{children}</span>
      </InputWrapper>
    </InputContainer>
  );
};

export default NumberInput;
